import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelTrackedRequest,
  fetchTrackedRequests,
} from '../logic/api';
import { subscribeRequestTrackingInvalidation } from '../logic/requestTrackingInvalidation';
import {
  isRequestTrackingStatusActive,
  runIfDocumentVisible,
} from '../logic/requestTrackingPoll';
import type {
  RequestTrackingApiConfig,
  RequestTrackingItem,
  RequestTrackingListFilters,
  RequestTrackingStatus,
} from '../logic/types';

export interface UseRequestTrackingOptions {
  filters: RequestTrackingListFilters;
  /**
   * Poll interval while any queued/running items exist.
   * Set `<= 0` to disable polling entirely (fetch on mount / invalidation / refreshSignal only).
   */
  pollIntervalMs?: number;
  apiConfig?: RequestTrackingApiConfig;
}

export interface UseRequestTrackingResult {
  items: RequestTrackingItem[];
  total: number;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  cancelRequest: (requestId: string) => Promise<void>;
  statusCounts: Record<RequestTrackingStatus, number>;
}

export function useRequestTracking({
  filters,
  pollIntervalMs = 10000,
  apiConfig,
}: UseRequestTrackingOptions): UseRequestTrackingResult {
  const [items, setItems] = useState<RequestTrackingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const refresh = useCallback(async () => {
    const isFirstLoad = !hasLoadedRef.current;
    if (!isFirstLoad) setRefreshing(true);
    setError(null);
    try {
      const result = await fetchTrackedRequests(filters, apiConfig);
      setItems(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch request updates');
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiConfig, filters]);

  const cancelRequest = useCallback(
    async (requestId: string) => {
      try {
        await cancelTrackedRequest(requestId, apiConfig);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to cancel this request right now.');
        throw err;
      }
    },
    [apiConfig, refresh]
  );

  useEffect(() => {
    hasLoadedRef.current = false;
    setLoading(true);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeRequestTrackingInvalidation(() => {
      void refresh();
    });
  }, [refresh]);

  const hasActiveRequests = useMemo(
    () => items.some((item) => isRequestTrackingStatusActive(item.status)),
    [items],
  );

  useEffect(() => {
    if (pollIntervalMs <= 0 || !hasActiveRequests) return;
    const timer = window.setInterval(() => {
      runIfDocumentVisible(() => {
        void refresh();
      });
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [pollIntervalMs, refresh, hasActiveRequests]);

  const statusCounts = useMemo<Record<RequestTrackingStatus, number>>(
    () =>
      items.reduce(
        (acc, item) => {
          acc[item.status] += 1;
          return acc;
        },
        {
          queued: 0,
          running: 0,
          completed: 0,
          failed: 0,
          cancelled: 0,
          unknown: 0,
        }
      ),
    [items]
  );

  return {
    items,
    total,
    loading,
    refreshing,
    error,
    refresh,
    cancelRequest,
    statusCounts,
  };
}
